import type { UseQueryOptions } from '@tanstack/react-query'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  GetAllPenyuluhStatusResponseDataType,
  InfiniteQueryOptions,
  PenyuluhStatusResponseDataType
} from './types'

const PENYULUH_STATUS_QUERY_KEY = (params: {
  page: number
  per_page: number
  search: string
}) => ['reference_penyuluh_status', { ...params }] as const

type PenyuluhStatusQueryKey = ReturnType<typeof PENYULUH_STATUS_QUERY_KEY>

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
  }>
): InfiniteQueryOptions<PenyuluhStatusQueryKey> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 10,
    search: opts?.search ?? ''
  }

  return {
    queryKey: PENYULUH_STATUS_QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async ({ pageParam }) => {
      try {
        const res = await api.get<GetAllPenyuluhStatusResponseDataType>(
          '/referensi/status-penyuluh',
          {
            params: {
              page: pageParam,
              per_page: params.per_page,
              nama:
                params.search.length > 3
                  ? `%${params.search.toUpperCase()}%`
                  : undefined
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const PENYULUH_STATUS_BY_ID_QUERY_KEY = (params: { id: string }) =>
  ['reference_penyuluh_status', params] as const

const byId = (opts: {
  id: string
}): UseQueryOptions<PenyuluhStatusResponseDataType> => ({
  queryKey: PENYULUH_STATUS_BY_ID_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const res = await api.get<PenyuluhStatusResponseDataType>(
        `/referensi/status-penyuluh/${opts.id}`
      )

      const data = res.data.data
      if (!data) {
        throw new Error(`ID status penyuluh ${opts.id} tidak dapat ditemukan.`)
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const penyuluhStatusReference = {
  queryKey: {
    all: PENYULUH_STATUS_QUERY_KEY,
    byId: PENYULUH_STATUS_BY_ID_QUERY_KEY
  },
  batched: getBatch,
  byId
}
